import { forwardRef, type HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, type IconType } from "../Icon";

export type BadgeVariant =
  | "green"
  | "white"
  | "white-fill"
  | "grey"
  | "blue"
  | "yellow"
  | "lime"
  | "red"
  | "indigo"
  | "zinc"
  | "orange"
  | "cyan"
  | "purple"
  | "amber"
  | "black";

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  icon?: IconType;
  iconPosition?: "left" | "right";
  iconSize?: number;
}

export const BadgeRoot = forwardRef<HTMLDivElement, BadgeProps>(
  ({ variant, icon, iconSize, iconPosition, ...divProps }, ref) => {
    const variantClasses =
      variant &&
      {
        green:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
        white:
          "border border-grey-700 bg-white text-grey-700 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200",
        "white-fill":
          "border border-zinc-300 bg-white text-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
        grey: "bg-gray-500 text-white dark:bg-zinc-700 dark:text-zinc-100",
        blue: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
        yellow:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300",
        lime: "bg-lime-100 text-lime-700 dark:bg-lime-950/40 dark:text-lime-300",
        red: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
        indigo:
          "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300",
        zinc: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200",
        orange:
          "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
        cyan: "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300",
        purple:
          "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300",
        amber:
          "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
        black: "bg-zinc-800 text-white dark:bg-zinc-700 dark:text-zinc-100",
      }[variant];

    return (
      <div
        ref={ref}
        {...divProps}
        className={twMerge(
          "inline-flex items-center gap-2 rounded-3xl bg-gray-100 px-2 py-1 text-sm font-bold dark:bg-zinc-800 dark:text-zinc-200",
          variantClasses,
          divProps.className,
        )}
      >
        {icon && iconPosition !== "right" && (
          <Icon size={iconSize ?? 0.7} icon={icon} />
        )}
        {divProps.children}
        {icon && iconPosition === "right" && (
          <Icon size={iconSize ?? 0.7} icon={icon} />
        )}
      </div>
    );
  },
);

export const BadgeGreen = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="green" />
);
export const BadgeBlue = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="blue" />
);
export const BadgeBlack = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="black" />
);
export const BadgeWhite = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="white" />
);
export const BadgeWhiteFill = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="white-fill" />
);
export const BadgeGrey = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="grey" />
);
export const BadgeYellow = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="yellow" />
);
export const BadgeLime = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="lime" />
);
export const BadgeRed = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="red" />
);
export const BadgeIndigo = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="indigo" />
);
export const BadgeZinc = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="zinc" />
);
export const BadgeOrange = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="orange" />
);
export const BadgeCyan = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="cyan" />
);
export const BadgePurple = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="purple" />
);
export const BadgeAmber = (props: BadgeProps) => (
  <BadgeRoot {...props} variant="amber" />
);

export const Badge = BadgeRoot;

BadgeRoot.displayName = "BadgeRoot";
